/**
 * Editor de plantillas de evaluación. Solo admin.
 *
 * Cada plantilla tiene una lista de items: quant (con escala) o qual (texto).
 * El editor permite agregar/quitar items inline. La plantilla se versiona como
 * activa/inactiva (no borra) para preservar evaluaciones pasadas.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, forwardRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2, ArrowLeft, Settings2 } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  EVAL_VISIBILITIES,
  EVAL_VISIBILITY_LABELS,
  EVAL_RECURRENCES,
  EVAL_RECURRENCE_LABELS,
  APPLIES_TO_PROJECTS_OPTS,
  APPLIES_TO_PROJECTS_LABELS,
  questionnaireCreateSchema,
  type QuestionnaireCreateInput,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

type EvalLevel = (typeof EVAL_LEVELS)[number];
type EvalVisibility = (typeof EVAL_VISIBILITIES)[number];
type EvalRecurrence = (typeof EVAL_RECURRENCES)[number];
type AppliesToProjects = (typeof APPLIES_TO_PROJECTS_OPTS)[number];

interface Template {
  id: string;
  name: string;
  level: EvalLevel;
  visibility: EvalVisibility;
  description: string | null;
  active: boolean;
  recurrence: EvalRecurrence;
  isAnonymous: boolean;
  appliesToProjects: AppliesToProjects;
  items: Array<
    | {
        id: string;
        kind: "quant";
        prompt: string;
        scale: { min: number; max: number; minLabel?: string; maxLabel?: string };
        required: boolean;
      }
    | { id: string; kind: "qual"; prompt: string; placeholder?: string; required: boolean }
  >;
}

export const Route = createFileRoute("/admin/questionnaires")({
  component: TemplatesPage,
});

function TemplatesPage() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<{ templates: Template[] }>({
    queryKey: ["evaluation-templates", "all"],
    queryFn: () => api.get("/questionnaires?includeInactive=1"),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/questionnaires/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["evaluation-templates"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Plantillas de evaluación"
        subtitle="Gestiona las plantillas que usa el equipo para evaluar a los 3 niveles. Items pueden ser cuantitativos (escala) o cualitativos (texto)."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/evaluations" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            <button
              type="button"
              onClick={() => dialogRef.current?.showModal()}
              className="btn btn-primary"
            >
              <Plus className="size-4" /> Nueva plantilla
            </button>
          </div>
        }
      />

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {data && data.templates.length === 0 ? (
        <div className="card-elevated py-16 text-center">
          <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
            <Settings2 className="size-6 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-base-content mb-1">
            Sin plantillas todavía
          </h3>
          <p className="text-sm text-base-content/60 mb-5">
            Crea la primera para empezar a evaluar.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data?.templates.map((t) => (
            <li key={t.id} className="card-elevated p-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                  <span className={`badge badge-sm ${levelBadgeClass(t.level)}`}>
                    {EVAL_LEVEL_LABELS[t.level]}
                  </span>
                  <span className="badge badge-ghost badge-sm">
                    {EVAL_VISIBILITY_LABELS[t.visibility]}
                  </span>
                  <span className="badge badge-ghost badge-sm">
                    {EVAL_RECURRENCE_LABELS[t.recurrence]}
                  </span>
                  {t.isAnonymous && <span className="badge badge-warning badge-sm">anónima</span>}
                  {!t.active && <span className="badge badge-error badge-sm">inactiva</span>}
                </div>
                <h3 className="font-display font-semibold text-base-content">{t.name}</h3>
                {t.description && (
                  <p className="text-sm text-base-content/65 mt-1 line-clamp-2">{t.description}</p>
                )}
                <div className="text-xs text-base-content/55 mt-2">
                  {t.items.length} items · {t.items.filter((i) => i.kind === "quant").length}{" "}
                  cuantitativos · {t.items.filter((i) => i.kind === "qual").length} cualitativos
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => toggleActive.mutate({ id: t.id, active: !t.active })}
              >
                {t.active ? "Desactivar" : "Activar"}
              </button>
            </li>
          ))}
        </ul>
      )}

      <CreateTemplateDialog ref={dialogRef} onClose={() => dialogRef.current?.close()} />
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────

const CreateTemplateDialog = forwardRef<HTMLDialogElement, { onClose: () => void }>(
  function CreateTemplateDialog({ onClose }, ref) {
    const qc = useQueryClient();
    const {
      register,
      handleSubmit,
      control,
      reset,
      formState: { errors },
    } = useForm<QuestionnaireCreateInput>({
      resolver: zodResolver(questionnaireCreateSchema),
      defaultValues: {
        level: "team",
        visibility: "everyone",
        recurrence: "on_demand",
        isAnonymous: false,
        appliesToProjects: "none",
        items: [
          {
            id: cryptoRandom(),
            kind: "quant",
            prompt: "",
            scale: { min: 1, max: 5 },
            required: true,
          },
        ],
      },
    });

    const items = useFieldArray({ control, name: "items" });

    const create = useMutation({
      mutationFn: (input: QuestionnaireCreateInput) => api.post("/questionnaires", input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["evaluation-templates"] });
        reset();
        onClose();
      },
    });

    return (
      <dialog ref={ref} className="modal" onClose={() => reset()}>
        <div className="modal-box card-elevated max-w-2xl">
          <h3 className="text-lg font-display font-semibold mb-4">Nueva plantilla</h3>
          <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Nombre</span>
              </label>
              <input
                className="input input-bordered w-full"
                placeholder="Ej. Satisfacción del cliente trimestral"
                {...register("name")}
              />
              {errors.name && (
                <span className="text-xs text-error mt-1 block">{errors.name.message}</span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Nivel</span>
                </label>
                <select className="select select-bordered w-full" {...register("level")}>
                  {EVAL_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {EVAL_LEVEL_LABELS[l]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Visibilidad</span>
                </label>
                <select className="select select-bordered w-full" {...register("visibility")}>
                  {EVAL_VISIBILITIES.map((v) => (
                    <option key={v} value={v}>
                      {EVAL_VISIBILITY_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Recurrencia</span>
                </label>
                <select className="select select-bordered w-full" {...register("recurrence")}>
                  {EVAL_RECURRENCES.map((r) => (
                    <option key={r} value={r}>
                      {EVAL_RECURRENCE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Aplica a proyectos</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  {...register("appliesToProjects")}
                >
                  {APPLIES_TO_PROJECTS_OPTS.map((o) => (
                    <option key={o} value={o}>
                      {APPLIES_TO_PROJECTS_LABELS[o]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input
                type="checkbox"
                className="checkbox checkbox-primary checkbox-sm"
                {...register("isAnonymous")}
              />
              <span className="text-sm">Anónima — no se rastrea quién respondió</span>
            </label>

            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Descripción (opcional)</span>
              </label>
              <textarea
                rows={2}
                className="textarea textarea-bordered w-full"
                {...register("description")}
              />
            </div>

            <div className="space-y-2 pt-2 border-t border-base-300/60">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Items</h4>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    onClick={() =>
                      items.append({
                        id: cryptoRandom(),
                        kind: "quant",
                        prompt: "",
                        scale: { min: 1, max: 5 },
                        required: false,
                      })
                    }
                  >
                    <Plus className="size-3" /> Cuant.
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    onClick={() =>
                      items.append({
                        id: cryptoRandom(),
                        kind: "qual",
                        prompt: "",
                        required: false,
                      })
                    }
                  >
                    <Plus className="size-3" /> Cual.
                  </button>
                </div>
              </div>

              {items.fields.map((field, idx) => (
                <ItemRow
                  key={field.id}
                  idx={idx}
                  control={control}
                  register={register}
                  onRemove={() => items.remove(idx)}
                />
              ))}

              {errors.items && <span className="text-xs text-error">{errors.items.message}</span>}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-base-300/60">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
                {create.isPending && <span className="loading loading-spinner loading-xs" />}
                Crear plantilla
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>cerrar</button>
        </form>
      </dialog>
    );
  },
);

function ItemRow({
  idx,
  control,
  register,
  onRemove,
}: {
  idx: number;
  control: ReturnType<typeof useForm<QuestionnaireCreateInput>>["control"];
  register: ReturnType<typeof useForm<QuestionnaireCreateInput>>["register"];
  onRemove: () => void;
}) {
  const [kind] = useState<"quant" | "qual">(); // not used; pattern-style ok
  void kind;

  return (
    <Controller
      control={control}
      name={`items.${idx}.kind`}
      render={({ field }) => (
        <div className="rounded-md border border-base-300/60 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span
              className={
                field.value === "quant"
                  ? "badge badge-primary badge-xs"
                  : "badge badge-accent badge-xs"
              }
            >
              {field.value === "quant" ? "Cuant." : "Cual."}
            </span>
            <input
              className="input input-bordered input-sm flex-1"
              placeholder="Pregunta…"
              {...register(`items.${idx}.prompt`)}
            />
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              onClick={onRemove}
              aria-label="Quitar"
            >
              <Trash2 className="size-3.5 text-error" />
            </button>
          </div>
          {field.value === "quant" && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                className="input input-bordered input-xs"
                placeholder="min"
                {...register(`items.${idx}.scale.min`, { valueAsNumber: true })}
              />
              <input
                type="number"
                className="input input-bordered input-xs"
                placeholder="max"
                {...register(`items.${idx}.scale.max`, { valueAsNumber: true })}
              />
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-primary checkbox-xs"
              {...register(`items.${idx}.required`)}
            />
            <span className="text-xs">Obligatoria</span>
          </label>
        </div>
      )}
    />
  );
}

function cryptoRandom(): string {
  return crypto.randomUUID().slice(0, 8);
}

function levelBadgeClass(level: EvalLevel): string {
  switch (level) {
    case "client_user":
      return "badge-info";
    case "organizational":
      return "badge-secondary";
    case "team":
      return "badge-accent";
    case "project":
      return "badge-primary";
  }
}
